import { useEffect, useRef, useState, useCallback } from "react";
import { Instagram, Facebook, Menu, X } from "lucide-react";
// Supabase is dynamically imported to reduce initial bundle size
const getSupabase = () => import("@/integrations/supabase/client").then(m => m.supabase);
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ottoLogoFull from "@/assets/otto-logo-full-cropped.webp";
import ottoLogoNav from "@/assets/otto-logo-nav.webp";
import ottoIcon from "@/assets/otto-icon.webp";
import ottoPizzaNavIcon from "@/assets/otto-pizza-icon.webp";
import patternWheel from "@/assets/pattern-wheel.webp";

const categories = [
  "Mediterranean Bakery",
  "Woodfired Pizzas",
  "Fresh Pasta",
  "Sides & Add-ons",
  "Thick Shakes & Desserts",
  "Coffee & Drinks",
];

const Index = () => {
  const aboutRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({ email: "", message: "" });
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastScrollY = useRef(0);
  const scrollThreshold = 8;

  // Preload LCP image (pattern-wheel) for faster discovery
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = patternWheel;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  const handleScroll = useCallback(() => {
    const currentY = window.scrollY;
    const delta = currentY - lastScrollY.current;

    if (currentY <= 10) {
      setNavVisible(true);
    } else if (delta > scrollThreshold) {
      setNavVisible(false);
    } else if (delta < -scrollThreshold) {
      setNavVisible(true);
    }

    lastScrollY.current = currentY;
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
          }
        });
      },
      { threshold: 0.15 }
    );

    const targets = [
      ...(aboutRef.current?.querySelectorAll(".fade-target") ?? []),
      ...(menuRef.current?.querySelectorAll(".fade-target") ?? []),
      ...(contactRef.current?.querySelectorAll(".fade-target") ?? []),
    ];
    targets.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToAbout = () => {
    aboutRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const id = crypto.randomUUID();
      const supabase = await getSupabase();
      const { error } = await supabase.from("email_signups").insert({ email, id });
      if (error) {
        if (error.code === "23505") {
          toast("You're already on the list! 🍕");
          setSignupSuccess(true);
        } else {
          toast.error("We couldn't add you just now. Please try again.");
        }
      } else {
        setSignupSuccess(true);
        // Send confirmation email
        await supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "first-drop-confirmation",
            recipientEmail: email,
            idempotencyKey: `first-drop-${id}`,
          },
        });
      }
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isContactSubmitting) return;
    setIsContactSubmitting(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.functions.invoke("contact-form", {
        body: { name: "Guest", email: contactForm.email, message: contactForm.message },
      });
      if (error) {
        toast.error("We couldn't send that just now. Please try again.");
      } else {
        setContactSuccess(true);
        setContactForm({ email: "", message: "" });
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-secondary transition-all duration-300 ease-in-out ${navVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="w-full flex items-center justify-between px-6 py-4">
          <a href="#hero" className="flex-shrink-0 mr-auto">
            <img src={ottoPizzaNavIcon} alt="Otto Pizza" className="w-auto object-contain" style={{ height: '55px' }} />
          </a>
          <div className="hidden sm:flex items-center gap-8">
            <a href="#hero" className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">Home</a>
            <a href="#about" className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">About</a>
            <a href="#menu" className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">Menu</a>
            <a href="#contact" className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">Contact</a>
          </div>
          <button
            className="sm:hidden p-2 text-foreground/80 hover:text-primary transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-secondary bg-background/95 backdrop-blur-sm px-6 py-4 flex flex-col gap-4">
            <a href="#hero" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">Home</a>
            <a href="#about" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">About</a>
            <a href="#menu" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">Menu</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">Contact</a>
          </div>
        )}
      </nav>

      <main>
      {/* Hero */}
      <section id="hero" className="relative flex-col min-h-[92vh] overflow-hidden flex items-center justify-start my-[50px] mt-0" style={{ paddingTop: '120px', paddingBottom: '120px' }}>
        {/* Repeating wheel pattern */}
        <div className="absolute inset-0 pointer-events-none z-0 opacity-[0.05]" style={{
          backgroundImage: `url(${patternWheel})`,
          backgroundRepeat: 'repeat',
          backgroundSize: '140px',
          backgroundPosition: 'center',
        }} />

        {/* Cream overlay for "printed into paper" feel */}
        <div className="absolute inset-0 pointer-events-none z-[1]" style={{
          background: 'rgba(244, 241, 236, 0.94)',
        }} />

        <img
          src={ottoLogoFull}
          alt="Otto Pizza – Wood Fired Pizzeria"
          className="relative z-10 w-96 sm:w-[32rem] md:w-[38rem] object-contain opacity-[0.97]"
          width={672}
          height={455}
          fetchPriority="high"
          style={{ aspectRatio: '672 / 455' }}
        />

        <h1 className="relative z-10 sm:text-4xl md:text-4xl font-serif font-bold text-primary text-center mt-3 px-[11px] text-2xl" style={{ maxWidth: '780px', lineHeight: '1.25' }}>
          Six simple ingredients.<br />Done properly.
        </h1>

        <p className="relative z-10 text-lg sm:text-xl text-muted-foreground font-serif italic text-center mt-7" style={{ maxWidth: '560px' }}>
          Woodfired Mediterranean kitchen.<br />Bakery by day. Pizza by night.
        </p>

        {signupSuccess ? (
          <div className="relative z-10 text-center mt-10" style={{ maxWidth: '480px' }}>
            <p className="font-serif text-lg text-primary font-medium">Thanks — you're on the list.</p>
            <p className="text-sm text-muted-foreground mt-2">We'll let you know when the first drop is ready.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10 flex flex-col sm:flex-row items-center gap-4 w-full mt-10 px-[18px]" style={{ maxWidth: '480px' }}>
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-full h-12 px-5 bg-background border-secondary text-base w-full sm:flex-1"
              required
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 h-12 text-base font-semibold tracking-wide whitespace-nowrap"
            >
              {isSubmitting ? "Joining..." : "Join the first drop"}
            </Button>
          </form>
        )}

        <div className="relative z-10 flex items-center gap-5 mt-7">
          <a
            href="https://www.instagram.com/ottopizzeria.co/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:text-accent transition-colors"
          >
            Instagram
          </a>
          <span className="text-muted-foreground/40">•</span>
          <a
            href="https://www.facebook.com/profile.php?id=61572361854140"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:text-accent transition-colors"
          >
            Facebook
          </a>
        </div>

        {/* Micro-offer */}
        <div className="relative z-10 text-center space-y-1 mt-10">
          <p className="text-sm font-semibold text-accent">Opening offer for first 100 customers.</p>
          <p className="text-sm text-muted-foreground">First drop announced soon — limited batches daily.</p>
        </div>

        {/* Trust anchor */}
        <p className="relative z-10 text-xs tracking-widest uppercase text-muted-foreground font-medium mt-8">
          Woodfired oven <span className="text-accent/50">·</span> Real dough <span className="text-accent/50">·</span> No shortcuts
        </p>
      </section>

      {/* About */}
      <section id="about" ref={aboutRef} className="bg-secondary/40 px-6" style={{ paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="mx-auto" style={{ maxWidth: '820px' }}>
          <div className="grid md:grid-cols-2 items-center" style={{ gap: '70px' }}>
            <div className="fade-target opacity-0 flex justify-center">
              <img
                src={ottoIcon}
                alt="Otto Pizza icon"
                className="object-contain opacity-[0.92] w-full"
                width={637}
                height={425}
                style={{ aspectRatio: '637 / 425' }}
              />
            </div>
            <div className="fade-target opacity-0" style={{ maxWidth: '680px' }}>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-primary mb-4">
                Our Story
              </h2>
              <div className="flex items-center gap-2 mb-8">
                <div className="w-12 h-0.5 bg-accent rounded-full" />
                <div className="w-1.5 h-1.5 rounded-full bg-accent" />
              </div>
              <p className="text-foreground/80" style={{ lineHeight: 1.75, marginBottom: '22px' }}>
                At OTTO, we keep things simple, because that's where the difference is.
              </p>
              <p className="text-foreground/80" style={{ lineHeight: 1.75, marginBottom: '22px' }}>
                Our dough begins with six ingredients. Nothing added, nothing hidden.
              </p>
              <p className="text-foreground/80" style={{ lineHeight: 1.75, marginBottom: '22px' }}>
                Everything else is built with intention, Mediterranean flavours, familiar but refined, chosen for how they work together rather than how much we can add.
              </p>
              <p className="text-foreground/80" style={{ lineHeight: 1.75, marginBottom: '22px' }}>
                The day begins as a Mediterranean bakery, softer, slower, grounded in tradition.
              </p>
              <p className="text-foreground/80" style={{ lineHeight: 1.75, marginBottom: '22px' }}>
                By night, it shifts into a classic Italiano pizzeria, heat, char, and depth, built on the same foundation.
              </p>
              <p className="text-foreground/80" style={{ lineHeight: 1.75, marginBottom: '22px' }}>
                It's not about doing more.
              </p>
              <p className="text-foreground/80 font-medium text-primary" style={{ lineHeight: 1.75 }}>
                It's about doing it properly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Offering */}
      <section id="menu" ref={menuRef} className="px-6" style={{ paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="mx-auto text-center" style={{ maxWidth: '780px' }}>
          <div className="fade-target opacity-0">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-primary mb-4">
              Our Offering
            </h2>
            <div className="flex items-center justify-center gap-2 mb-12">
              <div className="w-8 h-0.5 bg-accent/60 rounded-full" />
              <div className="w-1.5 h-1.5 rounded-full bg-accent" />
              <div className="w-8 h-0.5 bg-accent/60 rounded-full" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 sm:gap-7" style={{ rowGap: '1.75rem' }}>
            {categories.map((cat, i) => (
              <div
                key={cat}
                className="fade-target opacity-0 py-8 px-5 rounded-xl border border-border/30 bg-secondary/15 transition-all duration-300 hover:-translate-y-0.5"
                style={{ animationDelay: `${i * 0.08}s`, boxShadow: '0 0 0 transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 20px -6px hsl(var(--primary) / 0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 0 transparent'; }}
              >
                <p className="font-serif text-lg sm:text-xl font-medium text-primary" style={{ letterSpacing: '0.5px' }}>{cat}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accent rule before contact */}
      <div className="w-full h-[2px] bg-accent/25" />

      {/* Contact Us */}
      <section id="contact" ref={contactRef} className="bg-secondary/40 px-6" style={{ paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="mx-auto" style={{ maxWidth: '640px' }}>
          <div className="fade-target opacity-0 text-center" style={{ marginBottom: '48px' }}>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-primary mb-4">
              Get in Touch
            </h2>
            <div className="flex items-center justify-center gap-2 mx-auto" style={{ marginBottom: '24px' }}>
              <div className="w-8 h-0.5 bg-accent/60 rounded-full" />
              <div className="w-1.5 h-1.5 rounded-full bg-accent" />
              <div className="w-8 h-0.5 bg-accent/60 rounded-full" />
            </div>
            <p className="text-muted-foreground" style={{ lineHeight: '1.7' }}>
              For enquiries, collaborations, or private bookings, reach out directly.
            </p>
            <a
              href="mailto:contact@ottopizza.com.au"
              className="inline-block text-primary/70 hover:text-accent transition-colors text-sm tracking-wide"
              style={{ marginTop: '12px' }}
            >
              contact@ottopizza.com.au
            </a>
          </div>
          {contactSuccess ? (
            <div className="fade-target opacity-0 animate-fade-in-up text-center" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
              <p className="font-serif text-xl text-primary font-medium">Thanks for reaching out.</p>
              <p className="text-sm text-muted-foreground mt-3">We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="fade-target opacity-0" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
              <Input
                type="email"
                placeholder="Your email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                className="rounded-lg h-14 px-5 bg-background border-border/40 text-base focus:border-primary/40"
                required
              />
              <Textarea
                placeholder="Your message"
                value={contactForm.message}
                onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                className="rounded-lg px-5 py-4 bg-background border-border/40 text-base min-h-[160px] resize-none focus:border-primary/40"
                required
              />
              <div className="text-center">
                <Button
                  type="submit"
                  disabled={isContactSubmitting}
                  className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-12 h-13 text-base font-semibold tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                >
                  {isContactSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
              <p className="text-center text-muted-foreground text-xs tracking-wide" style={{ marginTop: '8px' }}>
                We don't send noise. Only drops.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer accent line */}
      <div className="w-full h-[2px] bg-accent/25" />
      </main>

      {/* Footer */}
      <footer id="footer" className="bg-primary text-primary-foreground px-6" style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
        <div className="container mx-auto max-w-5xl">
          <div className="grid sm:grid-cols-[1.4fr_1fr_0.9fr] gap-12 sm:gap-16 items-start">
            <div className="flex flex-col items-center">
              <img
                src={ottoLogoFull}
                alt="Otto Pizza"
                className="object-contain brightness-0 invert w-full max-w-[460px]"
                width={1511}
                height={1023}
                style={{ aspectRatio: '1511 / 1023' }}
              />
              <div className="flex items-center justify-center gap-4 mt-5">
                <a
                  href="https://www.instagram.com/ottopizzeria.co/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-all duration-200 hover:-translate-y-0.5"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61572361854140"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-all duration-200 hover:-translate-y-0.5"
                >
                  <Facebook size={20} />
                </a>
              </div>
            </div>
            <div className="text-center sm:text-left pt-1">
              <h3 className="font-serif font-semibold text-sm uppercase tracking-widest mb-4 text-primary-foreground">
                Visit Us
              </h3>
              <p className="text-sm text-primary-foreground/80 leading-relaxed">
                Warehouse 32, 24 King William St<br />
                Broadmeadows
              </p>
              <div className="mt-4 flex flex-col gap-1.5">
                <a href="#about" className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors">About</a>
                <a href="#menu" className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors">Menu</a>
                <a href="#contact" className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors">Contact</a>
              </div>
            </div>
            <div className="text-center sm:text-left pt-1">
              <h3 className="font-serif font-semibold text-sm uppercase tracking-widest mb-4 text-primary-foreground">
                Hours
              </h3>
              <p className="text-sm text-primary-foreground/80 leading-relaxed mb-3">
                <span className="font-medium text-primary-foreground/90">Bakery</span><br />
                6:00am – 2:00pm
              </p>
              <p className="text-sm text-primary-foreground/80 leading-relaxed">
                <span className="font-medium text-primary-foreground/90">Pizza</span><br />
                4:00pm – 12:00am
              </p>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-primary-foreground/20 text-center text-xs text-primary-foreground/80">
            © {new Date().getFullYear()} Otto Pizza Co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
